import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => (c._id === customerId || c.customer_id === customerId));
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id || customer.customer_id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id || customer.customer_id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id || customer.customer_id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function ReviewOfLegalPackDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const authCtx = useContext(AuthContext);

  // Get customerId from URL query params (same as Purchase/Sale & Purchase forms)
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');

  // Get basicFormData from navigation state (for new lead creation - Option 1)
  const location = useLocation();
  const basicFormData = location.state?.basicFormData || null;

  const [conveyancingData, setConveyancingData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [mainLeadData, setMainLeadData] = useState(null); // Store main lead data (group_code, product_id, etc.)
  const [isPostcodeValid, setIsPostcodeValid] = useState(true); // Initially true for edit page (existing data)
  const [postcodeValue, setPostcodeValue] = useState('');
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      lender_panel_id: '',
      partner_id: '',

      // Info section
      ownershipType: ''
    }
  });

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' }
  ];

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch conveyancing details
  const fetchConveyancingDetails = async () => {
    console.log('🔍 === FETCHING REVIEW OF LEGAL PACK DETAILS ===');
    console.log('📍 Lead ID:', id);

    try {
      setLoading(true);

      // Use dedicated Review of Legal Pack API
      console.log('📊 Fetching Review of Legal Pack data...');
      const mainLeadResponse = await postRequest(`private/getReviewOfLegalPackDetails/${id}`, {}, authCtx.token);

      console.log('📦 API Response:', mainLeadResponse);

      if ((mainLeadResponse?.status === 200 || mainLeadResponse?.status === 'success') && mainLeadResponse?.data) {
        const lead = mainLeadResponse.data;
        console.log('✅ Found main lead data:', lead);
        console.log('👥 Main lead customers:', lead.customers);
        console.log('🏠 Address:', lead.address || lead.property_address);
        console.log('🏦 Lender Panel:', lead.lender_panel_id);
        console.log('📋 Ownership Type:', lead.ownership_type);
        console.log('📂 Group Code:', lead.group_code);
        console.log('📦 Product ID:', lead.product_id);

        setConveyancingData(lead);
        setMainLeadData(lead); // Store main lead data for payload

        // Auto-fill Customers from main lead
        console.log('👥 === AUTO-FILLING CUSTOMERS ===');

        let customersToSet = [];

        // Check if customers are already populated (objects) or just IDs
        if (lead.customers && Array.isArray(lead.customers) && lead.customers.length > 0) {
          const firstCustomer = lead.customers[0];

          if (typeof firstCustomer === 'object' && (firstCustomer._id || firstCustomer.customer_id)) {
            // Already populated
            customersToSet = lead.customers;
            console.log('✅ Using populated customers:', customersToSet.length);
          } else if (typeof firstCustomer === 'string') {
            // Customer IDs only - do not fetch individual data for Review of Legal Pack
            console.log('⚠️ Customers are IDs only, not fetching individual data');
            customersToSet = [];
          }
        }

        // Only set if not already set from URL customerId parameter
        if (customers.length === 0) {
          setCustomers(customersToSet);
          console.log('✅ Customers state updated with', customersToSet.length, 'customers');
        } else {
          console.log('⚠️ Customers already set (from URL), not overwriting');
        }

        // Populate property details and other fields
        console.log('📝 === POPULATING FORM FIELDS ===');

        // Property Address - Check multiple sources
        const address = lead.propertyDetails?.address || lead.property_address || lead.address;
        console.log('🏠 Address data:', address);

        // Lender Panel
        const lenderPanelId = lead.lender_panel_id
          ? (typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id)
          : '';
        console.log('🏦 Lender Panel ID:', lenderPanelId);

        // Partner
        const partnerId = lead.partner_id
          ? (typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id)
          : '';
        console.log('🤝 Partner ID:', partnerId);

        // Ownership Type
        const ownershipTypeValue = lead.ownership_type
          ? lead.ownership_type.toLowerCase()
          : '';
        console.log('📋 Ownership Type:', ownershipTypeValue);

        // Build form data object
        const formDataToSet = {
          line1: address?.line1 || address?.address1 || '',
          line2: address?.line2 || address?.address2 || '',
          town: address?.town || '',
          county: address?.county || address?.country || '',
          postcode: address?.postcode || '',
          lender_panel_id: String(lenderPanelId),
          partner_id: String(partnerId),
          ownershipType: ownershipTypeValue
        };

        console.log('📦 Form data to set:', formDataToSet);

        // Use reset() to set all values at once - this is more reliable than individual setValue calls
        reset(formDataToSet);
        setPostcodeValue(formDataToSet.postcode); // trigger postcode validation for prefilled value

        console.log('✅ Form reset with data complete');

        console.log('✅ === AUTO-FILL COMPLETE ===');
      }
    } catch (error) {
      console.error('❌ Error fetching conveyancing details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial customer from URL parameter (only if creating new lead without existing customers)
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      // Only use URL param if we don't have lead data yet (creating new lead)
      if (customerIdFromUrl && authCtx.token && customers.length === 0 && !conveyancingData) {
        console.log('📌 Fetching customer from URL parameter:', customerIdFromUrl);
        try {
          // Use same endpoint as other components (supports both MongoDB ID and custom ID)
          const customerRes = await postRequest(
            `private/getCustomerIndividualData/${customerIdFromUrl}`,
            {},
            authCtx.token
          );
          if (customerRes?.data) {
            console.log('✅ URL param customer fetched:', customerRes.data);
            setCustomers([customerRes.data]);
          }
        } catch (error) {
          console.error('❌ Error fetching initial customer:', error);
        }
      } else {
        console.log('⏭️ Skipping URL param customer fetch - using lead data instead');
      }
    };
    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token, conveyancingData]);

  useEffect(() => {
    if (id && id !== 'new' && authCtx.token) {
      fetchConveyancingDetails();
    }
  }, [id, authCtx.token]);

  // Fetch active lender panels and partners on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  const handleAddCustomer = (customer) => {
    const customerId = customer._id || customer.customer_id;
    if (!customers.some(c => (c._id === customerId || c.customer_id === customerId))) {
      setCustomers([...customers, customer]);
    }
  };

  const handleRemoveCustomer = (customerId) => {
    setCustomers(customers.filter(c => (c._id !== customerId && c.customer_id !== customerId)));
  };

  // 2-Step Postcode Validation (same as Purchase form)
  useEffect(() => {
    if (!postcodeValue || postcodeValue.trim().length === 0) {
      setIsPostcodeValid(false);
      return;
    }

    const checkPostcode = async () => {
      setCheckingPostcode(true);
      try {
        // Clear previous states before starting validation
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result:', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Invalid postcode (not in UK postcode database)
          console.log('❌ Step 1 Failed: Postcode does not exist');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);

          // Show error toast with API message
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Postcode exists
        console.log('✅ Step 1 Passed: Postcode exists');
        console.log('🔍 Step 2: Matching branch by postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result:', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Step 2 Passed: Branch matched
          console.log('✅ Step 2 Passed: Branch matched');
          setPostcodeMatch(matchRes.data.branch);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ Step 2 Failed: No branch serves this postcode area');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking postcode:', error);
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  const onSubmit = async (formData) => {
    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    try {
      const payload = {
        // Customer IDs
        customer_ids: customers.map(c => c._id || c.customer_id),

        // Property address
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          country: formData.county,
          postcode: formData.postcode
        },

        // Ownership type
        ownership_type: formData.ownershipType,

        // Lender Panel
        lender_panel_id: formData.lender_panel_id || null,

        // Partner
        partner_id: formData.partner_id || null,

        // Additional required fields
        acquisition: 'Direct',
        override_date: new Date()
      };

      // OPTION 1: New lead with basicFormData (passed from navigation)
      if (id === 'new' && basicFormData) {
        console.log('📋 New lead mode. Using basicFormData from navigation:', basicFormData);

        payload.group_code = basicFormData.group_code;
        payload.product_id = basicFormData.product_id;
        payload.created_id = basicFormData.created_id;
        payload.service_type = basicFormData.service_type || 'Review_Legal_Pack';
        payload.acquisition = basicFormData.acquisition;
        payload.override_date = basicFormData.override_date;
        payload.status = basicFormData.status || 'new';

        console.log('✅ Using basicFormData:');
        console.log('   - group_code:', payload.group_code);
        console.log('   - product_id:', payload.product_id);
        console.log('   - service_type:', payload.service_type);
      }
      // OPTION 2: Existing lead - use mainLeadData
      else if (mainLeadData) {
        console.log('📋 Existing lead mode. Using mainLeadData:', mainLeadData.lead_id);

        // Extract group_code (handle both object and string)
        if (mainLeadData.group_code) {
          payload.group_code = typeof mainLeadData.group_code === 'object'
            ? mainLeadData.group_code._id
            : mainLeadData.group_code;
          console.log('✅ group_code from mainLeadData:', payload.group_code);
        }

        // Extract product_id (handle both object and string)
        if (mainLeadData.product_id) {
          payload.product_id = typeof mainLeadData.product_id === 'object'
            ? mainLeadData.product_id._id
            : mainLeadData.product_id;
          console.log('✅ product_id from mainLeadData:', payload.product_id);
        }
      }

      // Use new API endpoint
      const actualLeadId = id === 'new' ? 'new' : id;
      const res = await postRequest(
        `private/saveConveyancingReviewOfLegalPackToLead/${actualLeadId}`,
        payload,
        authCtx.token
      );

      if (res?.status === 'success' || res?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id === 'new'
            ? 'Review of Legal Pack created and email sent successfully!'
            : 'Review of Legal Pack details updated successfully!'
        }).then(() => {
          // Always navigate to leads list
          navigate('/leads');
        });
      } else {
        throw new Error(res?.message || 'Failed to save details');
      }
    } catch (error) {
      console.error('Error saving details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            Edit Conveyancing #{conveyancingData?.reference || id} - Review of Legal Pack
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Review of Legal Pack Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Review of Legal Pack Details
              </Typography>
              
              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode (e.g. M41 0TZ)"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        sx={{ mb: 1 }}
                        onChange={(e) => {
                          field.onChange(e);
                          setPostcodeValue(e.target.value);
                        }}
                      />
                    )}
                  />

                  {/* Checking postcode status */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown when postcode not found */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setIsPostcodeValid(true);
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name} - {branch.address?.postcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>Ownership Type</Typography>
                  <Controller
                    name="ownershipType"
                    control={control}
                    rules={{ required: 'Ownership type is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.ownershipType}>
                        <Select {...field}>
                          <MenuItem value="">Select Ownership Type</MenuItem>
                          {ownershipTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.ownershipType && (
                          <Typography variant="caption" color="error">
                            {errors.ownershipType.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Customers Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {customers.map((customer) => (
                  <Box
                    key={customer._id || customer.customer_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveCustomer(customer._id || customer.customer_id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Customer
                </Button>
              </Box>
            </Box>

            {/* Lender Panel Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Lender Panel
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={panel._id}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Partner Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Partner
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomer}
        selectedCustomers={customers}
      />
    </>
  );
}